import { useRef, useState } from "react";
import { SARF_KABUL_ALANLARI, SARF_SERTIFIKA_ALANLARI, hedefEsit, sarfEtiketi, type GelenHedef, type SarfKarti } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  SARF_UCU,
  okumaDugmesi,
  okumaEylemleri,
  sarfFormu,
  sarfKarsilastirmaSatirlari,
  sarfKayitDegeri,
  sarfKayitMetni,
  sarfNedenMetni,
  sarfYenidenMetni,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarih, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 3 · PR 17: Documents kartının sarf sertifikası (EN 10204 3.1) ve sarf kabul formu "Document reading" bölümü (prototip belgeler--sarf-okuma, kart 1-6;
 * `WpqOkumaBolumu` iskeleti). Okuma yalnız eşlemenin, kuralların, "Use the reading"in ve lot kaydı yazımının kullandığı değerleri gösterir (sözleşme §10): sertifikada 8,
 * kabul formunda 5 değer. OASIS lotu Registers'ta birebir önerir; "Use this place" kartın bugünkü tür+yer kaydını çağırır, "Use the reading" yalnız dosyalama formunu
 * kesin değerlerle doldurur (sunucuya yazmaz). Dosyalamadan sonra lotun Received on'u (kabul formundan) ya da Diameter'ı (sertifikadan) "Record on the lot" ile, kişinin
 * onayıyla ve yalnız lotun alanı boşken yazılır; Registers'taki dolu değer değişmez. `salt` kapanmış satır içindir: form ve karar düğmeleri yok; "Record on the lot"
 * sunucunun eylemiyle çizilir. Okuma excel'inin adı kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";
const SATIR = "grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-2.5 py-2 @min-[40rem]:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1.3fr)]";
const ISO_GUN = /^\d{4}-\d{2}-\d{2}$/;
const TARIH_ALANLARI = ["tarih", "teslim_tarihi"];

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");

export interface SarfFormYardimi {
  /** "Use the reading": dosyalama formunun alanları (yalnız kesin değerler) */
  kullan: (alanlar: Record<string, string>) => void;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
}

export function SarfOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: SarfKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: SarfFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;
  const y = k.yer;
  const sertifika = k.cins === "sertifika";
  const lot = y?.lot ?? null;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = sarfKarsilastirmaSatirlari(k);
  const yeniden = sarfYenidenMetni(k.yenidenKarsilastirma, lot?.lot ?? (y?.yer?.tur === "kutuk" ? y.yer.anahtar : null));
  const toplamUyari = k.karsilastirma?.uyarilar.length ?? 0;
  const kayit = k.kayit;

  return (
    <div data-bolum="sarf-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the consumable document for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.sayfalar ? (sertifika ? cevir("Consumable certificate p.{0}", aralik(o.sayfalar)) : cevir("Receiving form p.{0}", aralik(o.sayfalar))) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 divide-y divide-line rounded-md border border-line text-sm" data-bolum="bl-parca">
            <li className={SATIR}>
              <span>
                <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                {o.sayfalar
                  ? <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: o.sayfalar.bas })} target="_blank" rel="noreferrer">p.{aralik(o.sayfalar)}</a>
                  : <span className="text-xs">—</span>}
              </span>
              <span className="min-w-0 [overflow-wrap:anywhere]">
                <span className="block text-xs text-ink-3">{cevir("Lot")}</span>
                <span className="block font-medium">{k.degerler.find((d) => d.alan === "lot")?.deger ?? "—"}</span>
              </span>
              <span className="col-span-2 min-w-0 [overflow-wrap:anywhere] @min-[40rem]:col-span-1">
                <span className="block text-xs text-ink-3">{cevir("Place")}</span>
                {lot && y && y.durum !== "yok" && <span className="mr-1.5">{cevir("Consumable lot {0} · {1}", lot.lot, lot.ad)}</span>}
                {y && y.durum !== "yok"
                  ? <Etiket tone={durumTonu(y.durum)}>{y.durum === "kisi" ? cevir("this place") : cevir("proposed")}</Etiket>
                  : <span className="text-ink-2">{y ? sarfNedenMetni(y.neden, k.degerler.find((d) => d.alan === "lot")?.deger ?? null) : "—"}</span>}
                {yardim && y?.durum === "oneri" && y.yer && !hedefEsit(y.yer, hedef) && (
                  <Buton kucuk className="mt-1" disabled={mesgul !== null} mesgul={mesgul === "yer"} onClick={() => calistir("yer", () => yardim.yerKaydet(y.yer!))}>{cevir("Use this place")}</Buton>
                )}
              </span>
            </li>
          </ul>

          {kayit && (
            <>
              <AltBaslik>{cevir("Record on the lot")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Field")}</th>
                    <th className={TABLO_BASI}>{cevir("On the document")}</th>
                    <th className={TABLO_BASI}>{cevir("In Registers")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    <tr data-alan={kayit.alan}>
                      <td className={TABLO_HUCRESI}>{kayit.alan === "teslim_tarihi" ? cevir("Received on") : cevir("Diameter")}</td>
                      <td className={TABLO_HUCRESI}>{sarfKayitDegeri(kayit.alan, kayit.okunan)}{sayfaLinki(kayit.sayfa)}</td>
                      <td className={TABLO_HUCRESI}>{lot ? cevir("Lot {0}: {1}", lot.lot, sarfKayitDegeri(kayit.alan, kayit.kayitta)) : "—"}</td>
                      <td className={TABLO_HUCRESI}>
                        {kayit.durum === "yazilabilir" && k.eylemler.kayit && kayit.okunan
                          ? (
                            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "kayit"} onClick={() => calistir("kayit", () => api.post(SARF_UCU.kayit(satirId), { alan: kayit.alan, deger: kayit.okunan }))}>
                              {cevir("Record on the lot")}
                            </Buton>
                          )
                          : sarfKayitMetni(kayit)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-ink-3">
                {cevir("“Record on the lot” writes the value of the document on the lot in Registers, only while the lot has none. A value already in Registers is never changed by a reading; a PM or System administrator can still edit it on Registers.")}
              </p>
            </>
          )}

          <AltBaslik>{sertifika ? cevir("Consumable certificate") : cevir("Receiving form")}</AltBaslik>
          <ul className="space-y-0.5 text-sm">
            {(sertifika ? SARF_SERTIFIKA_ALANLARI : SARF_KABUL_ALANLARI).map((a) => {
              const d = k.degerler.find((x) => x.alan === a.alan);
              const deger = TARIH_ALANLARI.includes(a.alan) && d?.deger && ISO_GUN.test(d.deger) ? tarih(d.deger) : d?.deger;
              return (
                <li key={a.alan} className="flex gap-2">
                  <Nokta k={d?.kesinlik ?? "bos"} />
                  <span className="w-[10rem] shrink-0 text-ink-2">{cevir(sarfEtiketi(a.alan))}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                    {sayfaLinki(d?.sayfa ?? null)}
                    {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                  </span>
                </li>
              );
            })}
          </ul>

          {yardim && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              <Buton kucuk disabled={mesgul !== null} onClick={() => yardim.kullan(sarfFormu(k))}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {sertifika
                  ? cevir("“Use the reading” fills Doc no., Manufacturer / supplier and Date with the certain values of the certificate; uncertain and empty values stay empty for you to type. Nothing is filed until you confirm.")
                  : cevir("“Use the reading” fills Date with the certain receiving date of the form; an uncertain or empty date stays empty for you to type. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {sertifika
              ? cevir("Not read: trade name, packaging and quantity, chemical analysis, mechanical values, production month, the signers and the PO number. Not checked yet: the WPS filler list (SARF-03, waits for WPS reading), use of the lot before it was received (SARF-04, waits for the historical-entry list) and the diameter on weld maps (SARF-05, the weld map diameter is not read).")
              : cevir("Not read: the address line, quantities, the other boxes and the signature. Not checked yet: use of the lot before it was received (SARF-04, waits for the historical-entry list).")}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {(karsilastirmaSatirlari.length > 0 || yeniden) && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
              {yeniden && (
                <li className="flex gap-2">
                  <Nokta k="kesin" />
                  <span className="min-w-0">{yeniden}</span>
                </li>
              )}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <ul className="space-y-1.5">
                  {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                </ul>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(SARF_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(SARF_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(SARF_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
